import { useEffect, useState } from "react";
import { Check, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useRefreshSettings } from "@/hooks/useSettings";
import { MediaField } from "./AdminModals";

const SITE_FIELDS = [["association_name", "Association name"], ["short_name", "Short name"], ["tagline", "Tagline"], ["email", "Email"], ["phone", "Phone"], ["address", "Address"], ["office_hours", "Office hours"], ["footer_text", "Footer text"], ["copyright", "Copyright line"], ["instagram", "Instagram URL"], ["linkedin", "LinkedIn URL"], ["youtube", "YouTube URL"], ["facebook", "Facebook URL"], ["whatsapp", "WhatsApp number (with country code)"], ["map_query", "Map location (address searched on Google Maps)"]];
const DEV_FIELDS = [["developer_name", "Developer / studio name"], ["developer_url", "Studio website URL"], ["developer_credit", "Footer credit line"]];
const PAY_FIELDS = [["upi_id", "UPI ID"], ["account_name", "Account name"], ["bank_name", "Bank name"], ["account_number", "Account number"], ["ifsc", "IFSC code"], ["note", "Instructions shown to members"]];

export const AdminSettings = () => {
  const [s, setS] = useState(null);
  const [status, setStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const refreshSettings = useRefreshSettings();
  useEffect(() => { api("/admin/settings").then(setS).catch(e => setStatus(e.message)); }, []);
  if (!s) return <div className="empty-state" data-testid="settings-loading">{status || "Loading settings…"}</div>;

  const set = (k, v) => setS({ ...s, [k]: v });
  const setPay = (k, v) => setS({ ...s, payment_instructions: { ...s.payment_instructions, [k]: v } });
  const setPlan = (i, k, v) => setS({ ...s, membership_plans: s.membership_plans.map((p, j) => (j === i ? { ...p, [k]: v } : p)) });
  const addPlan = () => setS({ ...s, membership_plans: [...s.membership_plans, { key: `plan-${Date.now().toString(36)}`, label: "New plan", amount: 500, currency: "INR", duration_months: 12, description: "" }] });
  const removePlan = i => setS({ ...s, membership_plans: s.membership_plans.filter((_, j) => j !== i) });

  const save = async e => {
    e.preventDefault(); setSaving(true); setStatus("");
    try { setS(await api("/admin/settings", { method: "PUT", body: JSON.stringify(s) })); setStatus("Settings saved."); refreshSettings(); }
    catch (ex) { setStatus(ex.message); }
    finally { setSaving(false); }
  };

  return (
    <form className="settings-form" onSubmit={save} data-testid="admin-settings-form">
      {status && <div className={status.includes("saved") ? "success-message" : "error-message"} data-testid="settings-status">{status}</div>}
      <section className="account-card">
        <h3>Site identity & contact</h3>
        <div className="admin-modal-fields">
          <MediaField k="logo" label="Logo" value={s.logo} onChange={v => set("logo", v)} />
          {SITE_FIELDS.map(([k, l]) => (
            <label key={k}>{l}<input value={s[k] || ""} onChange={e => set(k, e.target.value)} data-testid={`settings-${k}`} /></label>
          ))}
        </div>
      </section>
      <section className="account-card">
        <div className="section-head" style={{ marginBottom: 14 }}>
          <h3>Membership plans & fees</h3>
          <button type="button" className="button button-ghost dark" onClick={addPlan} data-testid="settings-add-plan"><Plus size={14} /> Add plan</button>
        </div>
        {s.membership_plans.map((p, i) => (
          <div className="plan-editor" key={p.key} data-testid={`plan-editor-${p.key}`}>
            <label>Label<input value={p.label} onChange={e => setPlan(i, "label", e.target.value)} data-testid={`plan-label-${p.key}`} /></label>
            <label>Amount (₹)<input type="number" min="0" value={p.amount} onChange={e => setPlan(i, "amount", e.target.value)} data-testid={`plan-amount-${p.key}`} /></label>
            <label>Validity (months, 0 = lifetime)<input type="number" min="0" value={p.duration_months} onChange={e => setPlan(i, "duration_months", e.target.value)} data-testid={`plan-duration-${p.key}`} /></label>
            <label className="wide">Description<input value={p.description || ""} onChange={e => setPlan(i, "description", e.target.value)} data-testid={`plan-description-${p.key}`} /></label>
            <button type="button" className="link danger" onClick={() => removePlan(i)} disabled={s.membership_plans.length <= 1} data-testid={`plan-remove-${p.key}`}><Trash2 size={13} /> Remove</button>
          </div>
        ))}
      </section>
      <section className="account-card">
        <h3>Website credits (footer & corner badge)</h3>
        <div className="admin-modal-fields">
          <MediaField k="developer_logo" label="Studio logo (shown bottom-right)" value={s.developer_logo} onChange={v => set("developer_logo", v)} />
          {DEV_FIELDS.map(([k, l]) => (
            <label key={k}>{l}<input value={s[k] || ""} onChange={e => set(k, e.target.value)} data-testid={`settings-${k}`} /></label>
          ))}
        </div>
      </section>
      <section className="account-card">
        <h3>Payment instructions (manual / bank transfer)</h3>
        <div className="admin-modal-fields">
          {PAY_FIELDS.map(([k, l]) => (
            <label key={k} className={k === "note" ? "media-field" : ""}>{l}<input value={s.payment_instructions?.[k] || ""} onChange={e => setPay(k, e.target.value)} data-testid={`settings-pay-${k}`} /></label>
          ))}
        </div>
      </section>
      <div className="admin-modal-actions">
        <button className="button button-gold" disabled={saving} data-testid="settings-save-button"><Check size={15} /> {saving ? "Saving…" : "Save settings"}</button>
      </div>
    </form>
  );
};
